/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// UI lib components
import {
  Box,
  ActionIcon,
  Divider,
  Group,
  ScrollArea,
  Stack,
} from '@mantine/core';

// Local helpers & utils
import { useSidePanel } from './store/sidePanelState';

// Types
import { SidePanelLayoutProps } from './types';

// Assets
import { IconChevronsLeft, IconChevronsRight } from '@tabler/icons-react';

// Styles
import * as classes from './styles/index.css';
import { palette } from '@ui-kit/colors';
import { getSidePanelWidth } from './styles/variables';
import { useEffect } from 'react';
import { useViewportSize } from '@mantine/hooks';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

export default function SidePanelLayout({
  children,
  title,
  sidePanelContent,
  controlled = false,
  open = false,
  isPageFile = false,
}: SidePanelLayoutProps) {
  /* ********************************** HOOKS ********************************* */
  const { width: screenWidth } = useViewportSize();
  const sidePanelWidth = getSidePanelWidth(screenWidth, isPageFile);

  // UI logic
  const sidePanel = useSidePanel();

  const width =
    (controlled ? 0 : 58) +
    (sidePanel.isCollapsed
      ? sidePanelWidth.collapsed
      : sidePanelWidth.expanded + (controlled ? 58 : 0));

  useEffect(() => {
    if (open) {
      sidePanel.open();
    }
  }, [open]);

  /* ******************************** RENDERING ******************************* */
  return (
    <Group
      className={classes.sidePanelLayout}
      w="100%"
      flex={1}
      align="start"
      gap={0}
      wrap="nowrap"
      style={{
        '--side-panel-width': `${width / 16}rem`,
        overflow: 'hidden',
      }}
    >
      <Stack
        flex={1}
        h="100%"
        miw={0} // prevent the stack to overflow, see https://defensivecss.dev/tip/flexbox-min-content-size/
      >
        {children}
      </Stack>

      <Divider orientation="vertical" />

      <Stack
        className={classes.sidePanel}
        align="stretch"
        h="100%"
        gap={0}
        w={width}
      >
        {sidePanel.isCollapsed ? (
          !controlled && (
            <Box py="sm" px="xs">
              <ActionIcon
                onClick={() => sidePanel.toggle()}
                variant="subtle"
                color={palette.blackroomLightGray2}
              >
                <IconChevronsLeft size={22} />
              </ActionIcon>
            </Box>
          )
        ) : (
          <>
            {title && (
              <Group
                justify="space-between"
                w="100%"
                py="sm"
                pr="xs"
                pl="sm"
                wrap="nowrap"
              >
                <Box flex={1} miw={0}>
                  {title}
                </Box>
                {!controlled && (
                  <ActionIcon
                    onClick={() => sidePanel.toggle()}
                    variant="subtle"
                    color={palette.blackroomLightGray2}
                  >
                    <IconChevronsRight size={22} />
                  </ActionIcon>
                )}
              </Group>
            )}

            <ScrollArea w="100%" className={classes.sidePanelContent}>
              {sidePanelContent}
            </ScrollArea>
          </>
        )}
      </Stack>
    </Group>
  );
}
